import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { Alert, Box, Button, Grid, MenuItem, Snackbar, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  createOssIntegrationEvent,
  fetchOssIntegrationEvents,
  resolveOssIntegrationEvent,
  retryOssIntegrationEvent,
} from "../../api/ossApi";
import { OssPageHeader } from "../../components/common/OssPageHeader";
import { OssPanel, OssSectionPanel } from "../../components/common/OssPanel";
import { OssIntegrationEventDialog } from "../../components/integrations/OssIntegrationEventDialog";
import { OssIntegrationEventList } from "../../components/integrations/OssIntegrationEventList";
import { useOssAsyncData } from "../../hooks/useOssAsyncData";

const fallbackData = { items: [], summary: {}, pagination: { page: 1, pageSize: 20, total: 0 } };

const metricItems = [
  ["FAILED", "실패", "#ef4444"],
  ["RETRY", "재시도", "#f59e0b"],
  ["PENDING", "대기", "#64748b"],
  ["SUCCESS", "성공", "#10b981"],
];

export const OssIntegrations = () => {
  const { group_code, project_code } = useParams();
  const [filters, setFilters] = useState({ status: "", systemCode: "", search: "" });
  const [query, setQuery] = useState(filters);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [toast, setToast] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { data, loading, error, setError, load } = useOssAsyncData({
    fetcher: () => fetchOssIntegrationEvents(group_code, project_code, query),
    fallbackData,
    errorMessage: "연계 이벤트를 불러오지 못했습니다.",
    deps: [group_code, project_code, query],
  });

  const run = async (fn, message) => {
    setError("");
    setSubmitting(true);
    try {
      await fn();
      setToast(message);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || "처리 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const updateFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const submitEvent = async (payload) => {
    setError("");
    setSubmitting(true);
    try {
      await createOssIntegrationEvent(group_code, project_code, payload);
      setDialogOpen(false);
      setToast("연계 이벤트를 등록했습니다.");
      await load();
    } catch (e) {
      setError(e.response?.data?.message || "연계 이벤트 등록 중 오류가 발생했습니다.");
      throw e;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack spacing={1.5}>
      <OssPageHeader
        title="연계 모니터"
        iconName="databaseIcon"
        actions={(
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setDialogOpen(true)}>
            이벤트 등록
          </Button>
        )}
      />

      {error && <Alert severity="warning">{error}</Alert>}

      <Grid container spacing={1.5}>
        {metricItems.map(([key, label, color]) => (
          <Grid item xs={6} md={3} key={key}>
            <OssPanel sx={{ p: 1.8, display: "flex", alignItems: "center", gap: 1.2 }}>
              <Box sx={{ width: 10, height: 42, borderRadius: 999, bgcolor: color }} />
              <Box>
                <Typography sx={{ fontSize: 13, color: "#64748b" }}>{label}</Typography>
                <Typography sx={{ mt: 0.2, fontSize: 27, fontWeight: 900, color: "#111827" }}>
                  {Number(data.summary?.[key] || 0).toLocaleString()}건
                </Typography>
              </Box>
            </OssPanel>
          </Grid>
        ))}
      </Grid>

      <OssSectionPanel title="연계 이벤트">
        <Stack spacing={1.2}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ xs: "stretch", md: "center" }}>
            <TextField
              select
              size="small"
              label="상태"
              value={filters.status}
              onChange={(e) => updateFilter("status", e.target.value)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="FAILED">실패</MenuItem>
              <MenuItem value="RETRY">재시도</MenuItem>
              <MenuItem value="PENDING">대기</MenuItem>
              <MenuItem value="PROCESSING">처리 중</MenuItem>
              <MenuItem value="SUCCESS">성공</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="시스템"
              value={filters.systemCode}
              onChange={(e) => updateFilter("systemCode", e.target.value)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="ERP">ERP</MenuItem>
              <MenuItem value="MES">MES</MenuItem>
              <MenuItem value="APS">APS</MenuItem>
              <MenuItem value="OTHER">기타</MenuItem>
            </TextField>
            <TextField
              size="small"
              label="검색"
              value={filters.search}
              onChange={(e) => updateFilter("search", e.target.value)}
              sx={{ flex: 1, minWidth: { md: 280 } }}
            />
            <Button variant="contained" startIcon={<SearchRoundedIcon />} onClick={() => setQuery({ ...filters })}>
              조회
            </Button>
          </Stack>
          <OssIntegrationEventList
            rows={data.items || []}
            loading={loading || submitting}
            onRetry={(row) =>
              run(() => retryOssIntegrationEvent(group_code, project_code, row.integration_event_id), "재처리 대기 상태로 변경했습니다.")
            }
            onResolve={(row) =>
              run(() => resolveOssIntegrationEvent(group_code, project_code, row.integration_event_id), "연계 이벤트를 해결 처리했습니다.")
            }
          />
        </Stack>
      </OssSectionPanel>

      <OssIntegrationEventDialog
        open={dialogOpen}
        loading={submitting}
        onClose={() => setDialogOpen(false)}
        onSubmit={submitEvent}
      />

      <Snackbar open={Boolean(toast)} autoHideDuration={2500} onClose={() => setToast("")} message={toast} />
    </Stack>
  );
};
